import * as Sentry from '@sentry/react'
import { Box, Button, Typography } from '@mui/material'
import { ReactNode, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface FallbackProps {
  resetError: () => void
}

const ErrorFallback = ({ resetError }: FallbackProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const failedPath = useRef(location.pathname)

  // Leaving the page that crashed (back button, a link) clears the error so the new page renders.
  useEffect(() => {
    if (location.pathname !== failedPath.current) resetError()
  }, [location.pathname, resetError])

  return (
    // Same layout and graphic as the 404 page (src/components/404.tsx)
    <Box className="flex min-h-screen items-center justify-center gap-8 bg-white p-8 tb:flex-col">
      <Box className="max-w-[520px]">
        <Typography className="mb-6 text-primary.600 text_md_semibold">Something went wrong</Typography>
        <Typography className="mb-4 display_xl_bold">We couldn&apos;t load this page</Typography>
        <Typography className="mb-8">Reloading usually fixes it. If it keeps happening, please contact us.</Typography>
        <Box className="flex flex-wrap gap-4">
          <Button variant="contained" onClick={() => window.location.reload()}>
            Reload
          </Button>
          <Button variant="outlined" onClick={() => navigate('/welcome')}>
            Back to Welcome
          </Button>
        </Box>
      </Box>
      <img src="/images/404.png" alt="" className="w-full max-w-[560px]" />
    </Box>
  )
}

// Without this, any error thrown while rendering unmounts the whole app and leaves a blank white page.
// Sentry renders `fallback` as a component, so it must be a stable one: an inline arrow would remount it on every
// navigation and lose the path it failed on.
const ErrorBoundary = ({ children }: { children: ReactNode }) => (
  <Sentry.ErrorBoundary fallback={ErrorFallback}>{children}</Sentry.ErrorBoundary>
)

export default ErrorBoundary
